import * as React from 'react'
import { ComponentProps } from 'react'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Link, useMatchRoute } from '@tanstack/react-router'
import { Logo, VerticalMenu } from '../..'
import { Sidebar } from '../../layout'
import useLayoutStore from '../../../stores/useLayoutStore/useLayoutStore'
import useWalkthroughStore from '../../../stores/useWalkthroughStore/useWalkthroughStore'
import { RenderMenuItemFnProps } from '../../common/MenuItem/MenuItem'
import { RoleRestrictedContent } from '@/components/molecules/RoleRestrictedContent/RoleRestrictedContent'
import { UserType } from '@/hooks'
import { AgentBrokerDropdown } from '@/components/organisms/AgentBrokerDropdown/AgentBrokerDropdown'
import { useFeatureFlag } from '@/components/providers/FeatureFlagsProvider/useFeatureFlag'

type MenuItemConfig = Omit<
    ComponentProps<typeof VerticalMenu>['menuItems'][0],
    'label' | 'id'
> & {
    translationKey: string
    walkthroughBadge?: string
}

type MenuItemsConfig = MenuItemConfig[]

const useMainMenuConfig = () => {
    const matchRoute = useMatchRoute()
    const isDataManagementEnabled = useFeatureFlag('dataManagement')
    const dataManagementMenuItem = isDataManagementEnabled
        ? [
              {
                  path: '/data-management',
                  translationKey: 'data_management',
                  icon: 'database',
              },
          ]
        : []

    const isOnBilling = !!matchRoute({
        to: '/billing-and-payments',
        fuzzy: true,
    })
    const isOnInvoices = !!matchRoute({
        to: '/invoices/$invoiceId',
        fuzzy: true,
    })

    const isBillingActive = isOnBilling || isOnInvoices

    const mainMenuConfig: MenuItemsConfig = [
        {
            path: '/dashboard',
            translationKey: 'dashboard',
            icon: 'grid_view',
        },
        {
            path: '/analytics',
            translationKey: 'analytics',
            icon: 'auto_graph',
            walkthroughBadge: 'Usage & cost reporting',
        },
        {
            path: '/billing-and-payments',
            translationKey: 'billing_and_payments',
            isActive: isBillingActive,
            icon: 'paid',
            walkthroughBadge: 'Accounting and billing reports',
        },
        {
            path: '/sustainability',
            translationKey: 'sustainability',
            icon: 'eco',
            walkthroughBadge: 'Emissions reports',
        },
        {
            path: '/active-management',
            translationKey: 'active_management',
            icon: 'wb_incandescent',
            walkthroughBadge: 'Interval data reports',
        },
        {
            path: '/reporting',
            translationKey: 'reporting',
            icon: 'leaderboard',
        },
        ...dataManagementMenuItem,
    ]

    return mainMenuConfig
}

type AppSidebarProps = React.PropsWithChildren & {
    something?: string
}

export const AppSidebar: React.FC<AppSidebarProps> = () => {
    const { t } = useTranslation('translations')
    const isSidebarCollapsed = useLayoutStore(
        (state) => state.isSidebarCollapsed
    )
    const { isActive: isWalkthroughActive, currentStep: walkthroughStep } =
        useWalkthroughStore()
    const mainMenuConfig = useMainMenuConfig()
    const menuItems = mainMenuConfig.map((item) => {
        const {
            translationKey: _translationKey,
            walkthroughBadge,
            ...passedItem
        } = item
        const label = t(`sidebar_menu.${item.translationKey}`)

        // Show badge only during step 1 of walkthrough
        const shouldShowBadge =
            isWalkthroughActive && walkthroughStep === 1 && walkthroughBadge

        return {
            ...passedItem,
            id: passedItem.path,
            label,
            walkthroughId: `sidebar-menu-item-${item.translationKey}`,
            badge: shouldShowBadge ? walkthroughBadge : undefined,
        }
    })

    const renderFn = (props: RenderMenuItemFnProps): React.ReactNode => {
        const { item, renderContents } = props

        return (
            <Link to={item.path}>
                {(linkProps) => {
                    return renderContents(item.isActive ?? linkProps.isActive)
                }}
            </Link>
        )
    }

    const logoWrapperClassnames = classnames(
        'transition-all duration-150 easing-in-out',
        'h-logoHeight w-full relative overflow-hidden [&_a]:block',
        {
            'px-3': !isSidebarCollapsed,
        }
    )

    const logoClassname = classnames(
        'absolute inset-0 h-full w-full object-contain object-left transition-all duration-150',
        {
            'pl-4': !isSidebarCollapsed,
        }
    )

    return (
        <Sidebar>
            <div className="space-y-6 px-4 py-6">
                <RoleRestrictedContent allowedRoles={[UserType.CUSTOMER]}>
                    <div className="space-y-2">
                        <div className={logoWrapperClassnames}>
                            <Link to="/">
                                <Logo className={logoClassname} />
                            </Link>
                        </div>
                    </div>
                </RoleRestrictedContent>
                <RoleRestrictedContent
                    allowedRoles={[UserType.AGENT, UserType.BROKER]}
                    strict={false}
                >
                    <AgentBrokerDropdown />
                    <div
                        className={classnames('transition-all duration-150', {
                            'border-t border-neutral-200': !isSidebarCollapsed,
                            'mx-auto w-auto border-t border-neutral-200':
                                isSidebarCollapsed,
                        })}
                    />
                </RoleRestrictedContent>
                <RoleRestrictedContent
                    allowedRoles={[UserType.CUSTOMER]}
                    strict={false}
                >
                    <nav
                        className="space-y-2 [&[class]]:!overflow-visible"
                        data-walkthrough="sidebar-menu"
                    >
                        <VerticalMenu
                            menuItems={menuItems}
                            variant={'primary'}
                            hideLabel={isSidebarCollapsed}
                            render={renderFn}
                            menuItemClassName={'rounded-lg'}
                        />
                    </nav>
                </RoleRestrictedContent>
                <RoleRestrictedContent
                    allowedRoles={[UserType.AGENT, UserType.BROKER]}
                >
                    <nav className="space-y-2" data-walkthrough="sidebar-menu">
                        <VerticalMenu
                            menuItems={Array(menuItems[0])}
                            variant={'primary'}
                            hideLabel={isSidebarCollapsed}
                            render={renderFn}
                            menuItemClassName={'rounded-lg'}
                            menuItemLabelWrapperClassname="w-[152px]"
                        />
                    </nav>
                </RoleRestrictedContent>
            </div>
        </Sidebar>
    )
}
